import { ArrowsClockwise, CheckSquare, Square } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { registerSnapshotDisclaimerEvent } from '../../../sources/auditLogs';
import {
    activateFlowSnapshot as activateSnapshot,
    deactivateAllFlowSnapshots,
    getSnapshots,
} from '../../../sources/flow';
import translations from '../../../translations';
import type { Paging } from '../../../types/api';
import type { FlowResponseAPI } from '../../../types/flow';
import { isPublic } from '../../../utils/flow';
import ButtonDefault from '../../buttons/ButtonDefault';
import ButtonPrimary from '../../buttons/ButtonPrimary';
import { SecurityIcon } from '../../flow/SecurityIcon';
import FormGroup from '../../generic/FormGroup';
import ConfirmModal from '../../generic/modal/ConfirmModal';
import Modal from '../../generic/modal/GenericModal';
import Table from '../../generic/Table';
import Loader from '../../loader/Loader';
import { useNotificationDispatch } from '../../notifications/NotificationProvider';
import { useTenant } from '../../TenantProvider';

interface Props {
    flowId: string;
    modalContainer: HTMLElement;
}

const FlowVersionManager = ({ flowId, modalContainer }: Props) => {
    const [isLoading, setIsLoading] = useState(false);
    const [snapshots, setSnapshots] = useState<FlowResponseAPI[]>([]);
    const [showDeactivateSnapshotConfirmation, setShowDeactivateSnapshotConfirmation] =
        useState(false);
    const [showActivateSnapshotConfirmation, setShowActivateSnapshotConfirmation] = useState(false);
    const [activateConfirmText, setActivateConfirmText] = useState('');
    const [selectedSnapshotId, setSelectedSnapshotId] = useState<string>('');
    const [selectedVersionId, setSelectedVersionId] = useState<string>('');
    const [paging, setPaging] = useState<Paging>({ page: 1, total: 0, pageSize: 10 });
    const dispatchNotification = useNotificationDispatch();
    const tenant = useTenant();

    // biome-ignore lint/correctness/useExhaustiveDependencies: Requires dedicated refactor to include all dependencies
    useEffect(() => {
        loadFlowSnapshots(paging.page);
    }, [paging.page]);

    const loadFlowSnapshots = async (page: number = paging.page) => {
        setIsLoading(true);

        try {
            const allSnapshots = await getSnapshots(flowId, page);
            setSnapshots(allSnapshots.items);
            setPaging(allSnapshots._meta);
        } catch (error) {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error,
                },
            });
        } finally {
            setIsLoading(false);
        }
    };

    const performActivation = async (
        flowId: string,
        flowVersionId: string,
        onFinally?: () => void,
    ) => {
        setIsLoading(true);
        try {
            await activateSnapshot(flowId, flowVersionId);
            await loadFlowSnapshots();
        } catch (error) {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error,
                },
            });
        } finally {
            setIsLoading(false);
            onFinally?.();
        }
    };

    const handleActivateClick = (
        flowId: string,
        flowVersionId: string,
        flowItem: FlowResponseAPI,
    ) => {
        if (
            isPublic(flowItem) &&
            (tenant.tenantSettings?.requirePublicFlowPublishConfirmation ?? true)
        ) {
            // Show confirmation dialog for public flows that have no identity provider
            setSelectedSnapshotId(flowId);
            setSelectedVersionId(flowVersionId);
            setActivateConfirmText('');
            setShowActivateSnapshotConfirmation(true);
        } else {
            // Activate directly for flows that are not public or have an identity provider
            performActivation(flowId, flowVersionId);
        }
    };

    const confirmActivation = () => {
        setShowActivateSnapshotConfirmation(false);
        performActivation(selectedSnapshotId, selectedVersionId, () => {
            setActivateConfirmText('');
            setSelectedSnapshotId('');
            setSelectedVersionId('');
        });
    };

    const deactivateAll = () => {
        deactivateAllFlowSnapshots(flowId)
            .then(async () => {
                setShowDeactivateSnapshotConfirmation(false);
                await loadFlowSnapshots();
                dispatchNotification({
                    type: 'notifySuccess',
                    payload: {
                        message: translations.VERSION_SECTION_deactivate_all_snapshots_success,
                    },
                });
            })
            .catch((error) => {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
            });
    };

    if (isLoading) {
        return <Loader message="Loading snapshots" />;
    }

    return (
        <>
            <h2>{translations.VERSION_SECTION_heading}</h2>
            <span className="body-text">
                <p>{translations.VERSION_SECTION_description_1}</p>
                <p>{translations.VERSION_SECTION_description_2}</p>
                <p>{translations.VERSION_SECTION_description_3}</p>
                <p>{translations.VERSION_SECTION_description_4}</p>
            </span>

            <span>
                <ButtonPrimary
                    className="btn-danger float-right margin-right"
                    title={translations.VERSION_SECTION_deactivate_all_snapshots}
                    onClick={() => setShowDeactivateSnapshotConfirmation(true)}
                >
                    {translations.VERSION_SECTION_deactivate_all_snapshots}
                </ButtonPrimary>
            </span>

            <ConfirmModal
                show={showDeactivateSnapshotConfirmation}
                title={translations.VERSION_SECTION_deactivate_all_snapshots}
                messages={[translations.VERSION_SECTION_deactivate_all_snapshots_confirm]}
                buttonStyle="danger"
                buttonCaption={translations.COMMON_confirm}
                onCancel={() => setShowDeactivateSnapshotConfirmation(false)}
                onConfirm={deactivateAll}
                container={modalContainer}
            />

            <Modal
                show={showActivateSnapshotConfirmation}
                onHide={() => setShowActivateSnapshotConfirmation(false)}
                container={modalContainer}
                className="config-modal"
                title="Activate Flow Snapshot"
                bodyClassName="auto-height"
                renderBody={() => (
                    <div>
                        <p>{translations.FLOW_public_snapshot_activation_warning_1}</p>
                        <p>{translations.FLOW_public_snapshot_activation_warning_2}</p>
                        <FormGroup
                            label={translations.FLOW_public_confirm_label}
                            htmlFor="activateConfirmInput"
                        >
                            <input
                                autoComplete="off"
                                id="activateConfirmInput"
                                type="text"
                                value={activateConfirmText}
                                onChange={(e) => setActivateConfirmText(e.target.value)}
                                className="form-control"
                                placeholder="Type PUBLIC here"
                            />
                        </FormGroup>
                    </div>
                )}
                renderFooter={() => (
                    <>
                        <button
                            className="btn btn-default"
                            type="button"
                            onClick={() => setShowActivateSnapshotConfirmation(false)}
                        >
                            Cancel
                        </button>
                        <ButtonPrimary
                            onClick={() => {
                                registerSnapshotDisclaimerEvent(flowId);
                                confirmActivation();
                            }}
                            name="Activate Confirm"
                            disabled={activateConfirmText !== 'PUBLIC'}
                        >
                            {translations.COMMON_confirm}
                        </ButtonPrimary>
                    </>
                )}
            />
            <div className="mw-items-header">
                <ButtonDefault
                    className="btn-sm"
                    title="Refresh Snapshots"
                    onClick={() => loadFlowSnapshots()}
                >
                    <ArrowsClockwise />
                </ButtonDefault>
            </div>

            <Table<FlowResponseAPI>
                wrapperClassName="margin-top"
                columns={[
                    {
                        renderHeader: () => translations.COMMON_TABLE_date_activated,
                        renderCell: ({ item }) => {
                            const formattedDate = new Date(item.dateCreated).toLocaleString(
                                undefined,
                                {
                                    dateStyle: 'medium',
                                    timeStyle: 'short',
                                },
                            );

                            return (
                                <div className="date-with-auth">
                                    <SecurityIcon
                                        isPublic={isPublic(item)}
                                        testId={`auth-${item.id.id || item.id.versionId}`}
                                        label={
                                            isPublic(item)
                                                ? translations.FLOW_auth_public_label
                                                : translations.FLOW_auth_secured_label
                                        }
                                    />
                                    <span>{formattedDate}</span>
                                </div>
                            );
                        },
                    },
                    {
                        renderHeader: () => translations.COMMON_TABLE_comment,
                        renderCell: ({ item }) => <div>{item.comment}</div>,
                        cellClassName: 'generic-cell-overflow',
                    },
                    {
                        renderHeader: () => translations.COMMON_TABLE_actions,
                        renderCell: ({ item }) =>
                            item.isDefault ? (
                                <div className="button-padding-left-container flex">
                                    <CheckSquare className="table-icon-medium" />
                                    <span className="padding-left-sml">Activated</span>
                                </div>
                            ) : (
                                <ButtonPrimary
                                    className="btn-sm"
                                    title="Activate Flow Snapshot"
                                    onClick={() =>
                                        handleActivateClick(item.id.id, item.id.versionId, item)
                                    }
                                >
                                    <Square className="table-icon-medium" />
                                    Activate Flow Snapshot
                                </ButtonPrimary>
                            ),
                        size: '14rem',
                    },
                ]}
                items={snapshots}
                rowClassName={() => 'generic-row button-height-container'}
                pagination={{
                    ...paging,
                    changePage: (page) => setPaging((prevPaging) => ({ ...prevPaging, page })),
                }}
            />
        </>
    );
};

export default FlowVersionManager;
